import { useCallback, useContext, useState } from 'react';

import moment from 'moment';

import { useUtils } from '@ezbobdev/ui-commons';
import { MainContext, useTranslation } from '@ezbobdev/ui-framework';
import * as SDK from '@ezbobdev/ui-sdk';

export const useContinue = (props, widgetName) => {
  const {
    children,
    willValidate,
    shouldValidate = true,
    didValidate,
    shouldCreateCase,
    willCreateUser,
    shouldCreateUser = true,
    didCreateUser,
    willUpdate,
    shouldUpdate = true,
    didUpdate,
    willQuote,
    shouldQuote = false,
    didQuote,
    willDecision,
    shouldDecision = false,
    didDecision,
    willMoveToStep,
    shouldMoveToStep = true,
    didMoveToStep,
    didFailed,
    overrideCase,
    overrideCreateCasePayload,
    stateDriven = false,
    direction = 'FORWARD',
    setShowPopup,
    allowLoginOnSignup = true,
    silentCreateUser = false,
    createUserBasedOn = 'password',
    ...rest
  } = props;

  const { actions, sdkActions } = useContext(MainContext);
  const { t } = useTranslation();
  const { getConfig } = useUtils(props, widgetName);

  const quoteFeeType = getConfig('quoteFeeType');
  const quoteRepaymentType = getConfig('quoteRepaymentType');
  const quotePolicy = getConfig('quotePolicy', 'V2');
  const decisionPolicy = getConfig('decisionPolicy', 'legacy');
  const className = getConfig('className');
  const align = getConfig('align', `center`);
  const disabled = getConfig('disabled', false);
  const size = getConfig('size', `large`);
  const type = getConfig('type', `submit`);
  const color = getConfig(`color`, `primary`);
  const variant = getConfig('variant', `contained`);
  const fullWidth = getConfig('fullWidth', false);

  const movingStep = useCallback(
    async (updateResults, decisionResults, quoteResults) => {
      if (shouldMoveToStep) {
        const step =
          typeof shouldMoveToStep === `function`
            ? await shouldMoveToStep(updateResults, decisionResults, quoteResults)
            : shouldMoveToStep;
        typeof willMoveToStep === `function` && willMoveToStep(step);

        switch (step) {
          case true:
            actions.userState.setCurrentStep(actions.userState.getCurrentStep() + 1);
            break;

          case false:
            typeof willMoveToStep === `function` && willMoveToStep(actions.userState.getCurrentStep());
            await sdkActions.case.updateCaseStep(actions.userState.getCurrentStepName());
            break;

          default:
            if (!step) break;

            if (!stateDriven) {
              await sdkActions.case.updateCaseStep(step);
            }

            actions.userState.setCurrentStepByName(step);
            break;
        }
        typeof didMoveToStep === `function` && didMoveToStep(step);
      }
      actions.view.togglePopupLoader(false);
    },
    [willMoveToStep, shouldMoveToStep],
  );

  const onClick = useCallback(async () => {
    actions.view.clearAlerts();

    if (actions.userState.getUserState('demoMode')) {
      await actions.case.moveToStep(shouldMoveToStep);
      return;
    }

    const stepDetails = direction !== 'BACKWARD' ? actions.userState.getCurrentStepDetails() : {};

    actions.view.togglePopupLoader(true, stepDetails.progressText);

    try {
      let isValidCase, createUserResults, updateResults, quoteResults, decisionResults;

      const shouldBeValidated = typeof shouldValidate === `function` ? await shouldValidate() : shouldValidate;

      if (shouldBeValidated) {
        typeof willValidate === `function` && (await willValidate());
        isValidCase = actions.caseValidation.validateCase();
        typeof didValidate === `function` && (await didValidate(isValidCase));

        if (!isValidCase) {
          actions.view.togglePopupLoader(false);
          return;
        }
      }

      const caseId = SDK.Tools.getOption('caseId');

      const shouldCaseBeCreated =
        !caseId &&
        (stepDetails.shouldCreateCase ||
          (typeof shouldCreateCase === `function` ? await shouldCreateCase() : shouldCreateCase));

      if (shouldCaseBeCreated) {
        await sdkActions.case.create(overrideCreateCasePayload);
      }

      const shouldBeUpdated =
        !shouldCaseBeCreated &&
        (typeof shouldUpdate === `function` ? await shouldUpdate(createUserResults) : shouldUpdate);

      // A page that statically declares both flags false is a read-only confirm page: nothing
      // to validate, nothing to save. Both must be literally false - pages that gate the update
      // at runtime (unread consent, failed validation) also resolve to false but are NOT
      // completing their step, and must never be recorded as completed.
      const isReadOnlyStepPage = shouldUpdate === false && shouldValidate === false;

      if (shouldBeUpdated && caseId) {
        if (typeof willUpdate === 'function') await willUpdate();
        let caseData = sdkActions.case.generateCase(true);
        if (typeof overrideCase === 'function') {
          caseData = await overrideCase(caseData);
        }
        updateResults = await sdkActions.case.update(true, caseData);
        if (typeof didUpdate === 'function') await didUpdate(updateResults);
      } else if (caseId && stateDriven && direction !== 'BACKWARD' && isReadOnlyStepPage) {
        // DCJ-11650: a read-only confirm page sends no case update, yet its Continue still
        // completes a BE step. Without a recorded wasStepCompleted the state machine's
        // step-completion guard rejects the forward progress and loops the user on the page.
        // Step-only patch: deliberately no form data and no overrideCase.
        updateResults = await sdkActions.case.updateCaseStep(actions.userState.getCurrentStepName(), true);
      }

      const shouldUserBeCreated =
        typeof shouldCreateUser === `function` ? await shouldCreateUser(isValidCase) : shouldCreateUser;

      if (shouldUserBeCreated) {
        typeof willCreateUser === `function` && (await willCreateUser());
        const brokerUuid = actions.case.getCaseValue('applicant.brokerUuid');
        if (!actions.userState.getUserState('authorities').includes('ROLE_BROKER') && !brokerUuid) {
          //update user. it should await for result to set new token for next calls
          if (createUserBasedOn === `password`) {
            createUserResults = await sdkActions.users.create(allowLoginOnSignup, silentCreateUser);
          } else {
            createUserResults = await sdkActions.case.createUserForCase();
          }
          typeof didCreateUser === `function` && (await didCreateUser(createUserResults));
        }
      }

      const shouldBeQuoted = typeof shouldQuote === `function` ? await shouldQuote(updateResults) : shouldQuote;

      if (shouldBeQuoted) {
        if (typeof willQuote === 'function') await willQuote();
        quoteResults = await SDK.Case.quote(caseId, quoteFeeType, quoteRepaymentType, quotePolicy);
        if (typeof didQuote === 'function') await didQuote(quoteResults);
      }

      const shouldBeDecisioned =
        typeof shouldDecision === `function` ? await shouldDecision(updateResults) : shouldDecision;

      if (shouldBeDecisioned) {
        const ez1Up = actions.settings.getUserConfigProp('ez1Up');
        if (!ez1Up) {
          setShowPopup && setShowPopup(true);
          return;
        }

        const hasMinimumRequirements = actions.case.minimumRequirements(name);
        if (!hasMinimumRequirements) {
          actions.view.setAlerts([{ message: t(`${name}.invalidMarketplaceItems`), type: 'error' }]);
          return;
        }

        const waitTime = actions.settings.getUserConfigProp(`widgetsConfig.${name}.waitTime`) || {
          seconds: 10,
        };
        const timeout = moment.duration(waitTime).asMilliseconds();

        if (typeof willDecision === 'function') await willDecision();
        decisionResults = await sdkActions.case.decision(caseId, timeout, decisionPolicy);
        if (typeof didDecision === 'function') await didDecision(decisionResults);

        if (decisionResults.data.decision === 'UNAVAILABLE') {
          //If broker, redirect him anyway
          const brokerUuid = actions.case.getCaseValue('applicant.brokerUuid');
          if (actions.userState.getUserState('authorities').includes('ROLE_BROKER') && !brokerUuid) {
            sdkActions.users.redirectToDashboard(true);
          } else {
            setShowPopup && setShowPopup(true);
          }
        } else {
          sdkActions.users.redirectToDashboard(true);
        }
      }

      await movingStep(updateResults, decisionResults, quoteResults);
    } catch (e) {
      if (!stateDriven) {
        await sdkActions.case.updateCaseStep(actions.userState.getCurrentStepName());
      }

      console.error(e);
      actions.view.togglePopupLoader(false);
      typeof didFailed === `function` && didFailed(e);
    }
  }, [
    willValidate,
    shouldValidate,
    didValidate,
    shouldCreateCase,
    willCreateUser,
    shouldCreateUser,
    didCreateUser,
    willUpdate,
    shouldUpdate,
    didUpdate,
    willQuote,
    shouldQuote,
    quoteFeeType,
    quoteRepaymentType,
    quotePolicy,
    didQuote,
    willDecision,
    shouldDecision,
    decisionPolicy,
    didDecision,
    willMoveToStep,
    shouldMoveToStep,
    didMoveToStep,
    didFailed,
    overrideCase,
    overrideCreateCasePayload,
    allowLoginOnSignup,
    silentCreateUser,
    stateDriven,
  ]);

  return {
    children,
    className,
    align,
    disabled,
    size,
    color,
    type,
    variant,
    fullWidth,
    onClick,
    ...rest,
  };
};
